import orgApi from '@api/orgApi';
import ItemEditor from '@components/itemEditor';
import OrgTypeSelect from '@components/orgTypeSelect';
import OrgUserList from '@components/orgUserList';
import useAuth from '@hooks/useAuth';
import { t } from '@i18n';
import { TextInput } from '@mantine/core';
import type { Org } from '@models/org';
import AlertList from '@routes/profile/orgs/-alertList';
import RoleList from '@routes/profile/orgs/-roleList';
import { createFileRoute } from '@tanstack/react-router';
import { createForm, LoadingBoundary } from 'cross-state/react';

export const Route = createFileRoute('/profile/orgs/$id')({
  component: OrgEditor,
});

const orgForm = createForm<Org>({
  defaultValue: {
    id: '',
    type: 'tenant',
    name: '',
  },
  validations: {
    type: { required: (x) => !!x },
    name: { required: (x) => !!x },
  },
});

function OrgEditor() {
  const { id: orgId } = Route.useParams();
  const { hasClaim } = useAuth();
  const isNew = orgId === 'new';

  return (
    <ItemEditor
      domain="orgs"
      api={orgApi}
      readonly={!hasClaim('orgs/write', !orgId || orgId === 'new' ? 'any' : orgId) || orgId === 'global'}
      form={orgForm}
      name={(org) => org.name}
      footer={
        <div
          css={{
            marginTop: 'var(--mantine-spacing-xl)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--mantine-spacing-md)',
          }}
        >
          {!isNew && hasClaim('roles/read', orgId) && <RoleList orgId={orgId} />}

          {!isNew && hasClaim('roles/read', orgId) && <AlertList orgId={orgId} />}

          <LoadingBoundary>{!isNew && <OrgUserList type="org" orgId={orgId} />}</LoadingBoundary>
        </div>
      }
    >
      <orgForm.Field
        name="type"
        render={(props) => (
          <OrgTypeSelect
            {...props}
            value={props.value ?? ''}
            label={t('orgs.properties.type.label')}
            withAsterisk
            autoComplete="off"
            readOnly={!isNew}
          />
        )}
      />

      <orgForm.Field
        name="name"
        render={(props) => (
          <TextInput
            {...props}
            value={props.value ?? ''}
            label={t('orgs.properties.name.label')}
            withAsterisk
            autoComplete="off"
            spellCheck={false}
          />
        )}
      />
    </ItemEditor>
  );
}
